/**
 * src/hooks/useAuthorization.ts
 *
 * React access to the authorization service. Panels call these instead of
 * comparing wallet addresses to stored metadata.
 *
 * All three hooks share one shape: `{ auth, loading, error, reload }`, and
 * `auth` is null until an answer arrives. Null denies — a control must not
 * be enabled while its authorization is still in flight, which is the
 * failure mode of every "default to true until proven otherwise" gate.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getObjectAuthorization,
  getObjectAuthorizations,
  getCountryAuthorization,
  getOrganizationAuthorization,
  getAuthorizationConfig,
  invalidateAuthorization,
  onAuthorizationInvalidated,
  type AuthorizationConfig,
} from '../v2/services/authorizationService';
import type {
  ObjectAuthorization,
  CountryAuthorization,
  OrganizationAuthorization,
} from '../v2/services/authorizationModel';

export interface AuthorizationState<T> {
  auth: T | null;
  loading: boolean;
  error: string | null;
  /** Drop the cached answer and ask again. Call after any write. */
  reload: () => void;
}

/**
 * Guard every async setState behind a mount check.
 *
 * These hooks are used inside overlays that close while a request is in
 * flight, which is the classic source of "setState on an unmounted
 * component" noise.
 */
function useMounted(): React.MutableRefObject<boolean> {
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  return mounted;
}

/**
 * Re-ask whenever anything invalidates authorization anywhere in the app.
 *
 * Without this, a panel holding a resolved answer in state would keep
 * showing it after a write in a different component dropped the cache — and
 * a reassignment completed in the binding dialog would leave a sibling panel
 * offering actions the chain has just started refusing.
 */
function useInvalidationNonce(): number {
  const [nonce, setNonce] = useState(0);
  useEffect(() => onAuthorizationInvalidated(() => setNonce((n) => n + 1)), []);
  return nonce;
}

// ── One object ───────────────────────────────────────────────────────────────

/**
 * What `wallet` may do to `objectId`.
 *
 * Passing a null object id or wallet is a normal state, not an error: it
 * means there is nothing to ask about yet, and the result is a clean
 * refusal rather than a spinner that never resolves.
 */
export function useObjectAuthorization(
  objectId: string | null | undefined,
  wallet: string | null | undefined,
): AuthorizationState<ObjectAuthorization> {
  const [auth, setAuth] = useState<ObjectAuthorization | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Single source of "ask again": `reload` invalidates, invalidation
  // broadcasts, and the broadcast bumps this. A separate local counter would
  // double every refetch.
  const nonce = useInvalidationNonce();
  const mounted = useMounted();

  useEffect(() => {
    if (!objectId || !wallet) {
      setAuth(null);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    getObjectAuthorization(objectId, wallet)
      .then((result) => {
        if (cancelled || !mounted.current) return;
        setAuth(result);
      })
      .catch((e: unknown) => {
        if (cancelled || !mounted.current) return;
        // A failed read leaves `auth` null, which denies. The message is
        // for the operator, so a disabled control is explainable rather
        // than mysterious.
        setAuth(null);
        setError(e instanceof Error ? e.message : 'Could not resolve authorization');
      })
      .finally(() => {
        if (!cancelled && mounted.current) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [objectId, wallet, nonce, mounted]);

  const reload = useCallback(() => {
    invalidateAuthorization(objectId ?? undefined);
  }, [objectId]);

  return { auth, loading, error, reload };
}

// ── Many objects ─────────────────────────────────────────────────────────────

export interface ObjectAuthorizationMapState {
  /** Keyed by 32-char hex object id, not UUID. Use `authFor` to look up. */
  map: Map<string, ObjectAuthorization>;
  loading: boolean;
  error: string | null;
  /** Accepts either a UUID or hex id. */
  authFor: (objectId: string | null | undefined) => ObjectAuthorization | null;
  reload: () => void;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function toHexId(value: string | null | undefined): string | null {
  const raw = String(value ?? '').trim();
  if (UUID_RE.test(raw)) return raw.replace(/-/g, '').toLowerCase();
  if (/^[0-9a-f]{32}$/i.test(raw)) return raw.toLowerCase();
  return null;
}

/**
 * Authorization for a list of objects, resolved in batches.
 *
 * The ids are joined into the effect dependency rather than passed as an
 * array, because a caller that builds `rows.map(r => r.id)` inline produces
 * a new array identity on every render and would otherwise re-request
 * forever.
 */
export function useObjectAuthorizations(
  objectIds: string[],
  wallet: string | null | undefined,
): ObjectAuthorizationMapState {
  const [map, setMap] = useState<Map<string, ObjectAuthorization>>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nonce = useInvalidationNonce();
  const mounted = useMounted();

  const key = objectIds.join(',');

  useEffect(() => {
    if (!wallet || !key) {
      setMap(new Map());
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    getObjectAuthorizations(key.split(','), wallet)
      .then((result) => {
        if (cancelled || !mounted.current) return;
        setMap(result);
      })
      .catch((e: unknown) => {
        if (cancelled || !mounted.current) return;
        setMap(new Map());
        setError(e instanceof Error ? e.message : 'Could not resolve authorization');
      })
      .finally(() => {
        if (!cancelled && mounted.current) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [key, wallet, nonce, mounted]);

  const authFor = useCallback(
    (objectId: string | null | undefined) => {
      const hex = toHexId(objectId);
      return hex ? map.get(hex) ?? null : null;
    },
    [map],
  );

  const reload = useCallback(() => { invalidateAuthorization(); }, []);

  return { map, loading, error, authFor, reload };
}

// ── Country ──────────────────────────────────────────────────────────────────

export function useCountryAuthorization(
  countryCode: string | number | null | undefined,
  wallet: string | null | undefined,
): AuthorizationState<CountryAuthorization> {
  const [auth, setAuth] = useState<CountryAuthorization | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nonce = useInvalidationNonce();
  const mounted = useMounted();

  const code = countryCode == null ? '' : String(countryCode);

  useEffect(() => {
    if (!code || !wallet) {
      setAuth(null);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    getCountryAuthorization(code, wallet)
      .then((result) => {
        if (!cancelled && mounted.current) setAuth(result);
      })
      .catch((e: unknown) => {
        if (cancelled || !mounted.current) return;
        setAuth(null);
        setError(e instanceof Error ? e.message : 'Could not resolve authorization');
      })
      .finally(() => {
        if (!cancelled && mounted.current) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [code, wallet, nonce, mounted]);

  const reload = useCallback(() => { invalidateAuthorization(); }, []);

  return { auth, loading, error, reload };
}

// ── Organization ─────────────────────────────────────────────────────────────

export function useOrganizationAuthorization(
  orgId: string | null | undefined,
  wallet: string | null | undefined,
): AuthorizationState<OrganizationAuthorization> {
  const [auth, setAuth] = useState<OrganizationAuthorization | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nonce = useInvalidationNonce();
  const mounted = useMounted();

  useEffect(() => {
    if (!orgId || !wallet) {
      setAuth(null);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    getOrganizationAuthorization(orgId, wallet)
      .then((result) => {
        if (!cancelled && mounted.current) setAuth(result);
      })
      .catch((e: unknown) => {
        if (cancelled || !mounted.current) return;
        setAuth(null);
        setError(e instanceof Error ? e.message : 'Could not resolve authorization');
      })
      .finally(() => {
        if (!cancelled && mounted.current) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [orgId, wallet, nonce, mounted]);

  const reload = useCallback(() => { invalidateAuthorization(); }, []);

  return { auth, loading, error, reload };
}

// ── Config ───────────────────────────────────────────────────────────────────

/**
 * Whether the service can answer at all, so a surface can explain itself
 * instead of rendering controls that would all be disabled.
 */
export function useAuthorizationConfig(): {
  config: AuthorizationConfig | null;
  loading: boolean;
} {
  const [config, setConfig] = useState<AuthorizationConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const mounted = useMounted();

  useEffect(() => {
    getAuthorizationConfig()
      .then((c) => { if (mounted.current) setConfig(c); })
      .catch(() => { if (mounted.current) setConfig(null); })
      .finally(() => { if (mounted.current) setLoading(false); });
  }, [mounted]);

  return { config, loading };
}
